import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import dayjs from "dayjs";
import type { CoReviewComment } from "../../shared/interfaces/CoReviewComment";
import {
  formatCommentAuthor,
  groupCommentsByThread,
} from "@/features/co-review/api/coReviewCommentAdapter";
import { stripCitationTokens } from "../../features/narrative-citations/parseCitations";
import { CommentReplyComposer } from "./CommentReplyComposer";

export interface PendingNarrativeComment {
  pendingId: string;
  charStart: number;
  charEnd: number;
  quotedText: string;
  commentText: string;
}

export type NarrativeMode = "co-edit" | "co-readonly" | "officer";

interface Props {
  narrativeIndex: number;
  text: string;
  comments: CoReviewComment[];
  pendingComments?: PendingNarrativeComment[];
  mode: NarrativeMode;
  onAddComment?: (input: {
    narrativeIndex: number;
    charStart: number;
    charEnd: number;
    quotedText: string;
    commentText: string;
  }) => void;
  onResolveComment?: (commentId: string) => void;
  onUnresolveComment?: (commentId: string) => void;
  onFixWithAiComment?: (commentId: string) => void | Promise<void>;
  onReplyComment?: (parentCommentId: string, text: string) => void | Promise<void>;
  /** The report's owning officer; used to tag each comment as CO vs Officer. */
  reportOwnerUserId?: string;
}

interface ResolvedAnchor {
  start: number;
  end: number;
  commentId?: string;
  pendingId?: string;
}

/**
 * Resolve a stored comment's char range against the current text. Tries, in order:
 *   1. Exact (charStart, charEnd) range — preferred when the CO supplied both.
 *   2. quotedText search — relocate if the text shifted.
 *   3. charStart only — highlight the word at that offset (legacy comments
 *      submitted before the path encoding was extended to #start-end).
 *
 * Returns null only if nothing usable can be derived; the comment will still
 * appear as an orphan card below the narrative.
 */
function resolveAnchor(
  text: string,
  charStart: number | undefined,
  charEnd: number | undefined,
  quotedText: string | undefined,
): { start: number; end: number } | null {
  if (
    typeof charStart === "number" &&
    typeof charEnd === "number" &&
    charStart >= 0 &&
    charEnd <= text.length &&
    charStart < charEnd
  ) {
    if (!quotedText || text.slice(charStart, charEnd) === quotedText) {
      return { start: charStart, end: charEnd };
    }
  }
  if (quotedText && quotedText.length > 0) {
    const idx = text.indexOf(quotedText);
    if (idx >= 0) {
      return { start: idx, end: idx + quotedText.length };
    }
  }
  if (
    typeof charStart === "number" &&
    charStart >= 0 &&
    charStart < text.length
  ) {
    // Walk left to the start of the word containing the offset, then right
    // to the end. If the offset is on whitespace, just mark a single char.
    let start = charStart;
    while (start > 0 && /\S/.test(text[start - 1])) start--;
    let end = charStart;
    while (end < text.length && /\S/.test(text[end])) end++;
    if (end === start) {
      end = Math.min(start + 1, text.length);
    }
    if (end > start) {
      return { start, end };
    }
  }
  return null;
}

interface Segment {
  kind: "text" | "mark";
  start: number;
  end: number;
  anchors: ResolvedAnchor[];
}

function buildSegments(text: string, anchors: ResolvedAnchor[]): Segment[] {
  if (anchors.length === 0) {
    return text ? [{ kind: "text", start: 0, end: text.length, anchors: [] }] : [];
  }
  // Build event list for a sweep.
  const cuts = new Set<number>([0, text.length]);
  for (const a of anchors) {
    cuts.add(a.start);
    cuts.add(a.end);
  }
  const sorted = Array.from(cuts)
    .filter((n) => n >= 0 && n <= text.length)
    .sort((a, b) => a - b);
  const segments: Segment[] = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const start = sorted[i];
    const end = sorted[i + 1];
    if (start === end) continue;
    const covering = anchors.filter((a) => a.start <= start && a.end >= end);
    segments.push({
      kind: covering.length > 0 ? "mark" : "text",
      start,
      end,
      anchors: covering,
    });
  }
  return segments;
}

export const NarrativeWithInlineComments: React.FC<Props> = ({
  narrativeIndex,
  text,
  comments,
  pendingComments = [],
  mode,
  onAddComment,
  onResolveComment,
  onUnresolveComment,
  onFixWithAiComment,
  onReplyComment,
  reportOwnerUserId,
}) => {
  const theme = useTheme();
  // Comment ranges are a visible-prose contract. Citation markers are private
  // metadata, so neither rendering nor newly persisted selection offsets may
  // include them. Historical offsets from pre-citation narratives remain
  // compatible; quotedText, when present, still relocates shifted anchors.
  const displayText = useMemo(() => stripCitationTokens(text), [text]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [popover, setPopover] = useState<{
    anchorEl: HTMLElement;
    charStart: number;
    charEnd: number;
    quotedText: string;
    draft: string;
  } | null>(null);
  const [isFixing, setIsFixing] = useState(false);
  const [orphanReplyingTo, setOrphanReplyingTo] = useState<string | null>(null);
  // Per-thread collapse override for the inline-comment cards. Default state is
  // "collapsed iff resolved"; clicking the chevron records an explicit override.
  const [collapseOverride, setCollapseOverride] = useState<
    Record<string, boolean>
  >({});
  const replyEnabled =
    !!onReplyComment && (mode === "officer" || mode === "co-edit");

  const isCollapsed = (parentId: string, resolved: boolean): boolean =>
    collapseOverride[parentId] ?? resolved;

  const toggleCollapsed = (parentId: string, resolved: boolean) => {
    const current = isCollapsed(parentId, resolved);
    setCollapseOverride((prev) => ({ ...prev, [parentId]: !current }));
    if (!current && orphanReplyingTo === parentId) setOrphanReplyingTo(null);
  };

  // Resolving/unresolving clears the manual override so the thread snaps to
  // its default visibility (collapsed when resolved, expanded when not).
  const clearCollapseOverride = (parentId: string) => {
    setCollapseOverride((prev) => {
      if (!(parentId in prev)) return prev;
      const next = { ...prev };
      delete next[parentId];
      return next;
    });
  };

  const handleResolveClick = (commentId: string) => {
    clearCollapseOverride(commentId);
    if (orphanReplyingTo === commentId) setOrphanReplyingTo(null);
    onResolveComment?.(commentId);
  };

  const handleUnresolveClick = (commentId: string) => {
    clearCollapseOverride(commentId);
    onUnresolveComment?.(commentId);
  };

  const handleFixClick = async (commentId: string) => {
    if (!onFixWithAiComment) return;
    setIsFixing(true);
    try {
      await onFixWithAiComment(commentId);
    } finally {
      setIsFixing(false);
    }
  };

  // Clicking a highlight scrolls the matching inline thread card into view.
  // Card lives inside the narrative container itself now, so scope the query
  // to containerRef.
  const scrollToComment = (commentId: string) => {
    const root = containerRef.current;
    if (!root) return;
    const card = root.querySelector(
      `[data-co-thread-id="${commentId}"]`,
    ) as HTMLElement | null;
    if (!card) return;
    card.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const anchorsByComment = useMemo(() => {
    const result: ResolvedAnchor[] = [];
    for (const c of comments) {
      // Replies share their parent's anchor; the parent renders the highlight,
      // and the popover surfaces the thread. Skip replies here so we don't
      // double-anchor the same range.
      if (c.parentCommentId) continue;
      // resolveAnchor handles range, quotedText, and charStart-only fallbacks.
      // Comments without any positional info won't anchor and fall through to
      // the orphan list below the narrative.
      if (typeof c.charStart !== "number" && !c.quotedText) {
        continue;
      }
      const quotedText = stripCitationTokens(c.quotedText);
      const a = resolveAnchor(
        displayText,
        c.charStart,
        c.charEnd,
        quotedText,
      );
      if (a) {
        result.push({ ...a, commentId: c.commentId });
      }
    }
    return result;
  }, [comments, displayText]);

  // All top-level threads (with their replies) render inline below the
  // narrative. Highlights in the text act as visual anchors; clicking one
  // scrolls to the matching card.
  const orphanThreads = useMemo(
    () => groupCommentsByThread(comments),
    [comments],
  );

  const anchorsByPending = useMemo(
    () =>
      pendingComments.flatMap((p): ResolvedAnchor[] => {
        const anchor = resolveAnchor(
          displayText,
          p.charStart,
          p.charEnd,
          stripCitationTokens(p.quotedText),
        );
        return anchor ? [{ ...anchor, pendingId: p.pendingId }] : [];
      }),
    [displayText, pendingComments],
  );

  const allAnchors = useMemo(
    () => [...anchorsByComment, ...anchorsByPending],
    [anchorsByComment, anchorsByPending],
  );

  const segments = useMemo(
    () => buildSegments(displayText, allAnchors),
    [displayText, allAnchors],
  );

  const computeGlobalOffset = (node: Node, nodeOffset: number): number | null => {
    let el: HTMLElement | null =
      node.nodeType === Node.TEXT_NODE
        ? node.parentElement
        : (node as HTMLElement);
    while (el && el !== containerRef.current) {
      const start = el.dataset?.start;
      if (start != null) {
        return Number(start) + nodeOffset;
      }
      el = el.parentElement;
    }
    return null;
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== "co-edit") return;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (!sel || sel.isCollapsed) return;
    if (sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!containerRef.current || !containerRef.current.contains(range.commonAncestorContainer)) {
      return;
    }
    const startGlobal = computeGlobalOffset(range.startContainer, range.startOffset);
    const endGlobal = computeGlobalOffset(range.endContainer, range.endOffset);
    if (startGlobal == null || endGlobal == null) return;
    const [s, e] =
      startGlobal < endGlobal ? [startGlobal, endGlobal] : [endGlobal, startGlobal];
    if (s === e) return;
    const quoted = displayText.slice(s, e);
    if (!quoted.trim()) return;

    // Anchor popover at the click position so it appears near the cursor end.
    setPopover({
      anchorEl: event.currentTarget,
      charStart: s,
      charEnd: e,
      quotedText: quoted,
      draft: "",
    });
    // Pin a virtual anchor by setting position via the popover's anchorPosition.
    // We use anchorReference="anchorPosition" below.
    pendingMouseRef.current = { x: event.clientX, y: event.clientY };
  };

  const pendingMouseRef = useRef<{ x: number; y: number } | null>(null);

  const handleSubmitPopover = () => {
    if (!popover || !popover.draft.trim() || !onAddComment) return;
    onAddComment({
      narrativeIndex,
      charStart: popover.charStart,
      charEnd: popover.charEnd,
      quotedText: popover.quotedText,
      commentText: popover.draft.trim(),
    });
    setPopover(null);
    if (typeof window !== "undefined") window.getSelection()?.removeAllRanges();
  };

  const renderMark = (seg: Segment, key: number) => {
    const hasUnresolved = seg.anchors.some((a) => {
      if (a.commentId) {
        const c = comments.find((cc) => cc.commentId === a.commentId);
        return c && !c.resolvedAt;
      }
      return true; // pending always counts as unresolved
    });
    const color = hasUnresolved
      ? theme.palette.warning.main
      : theme.palette.success.main;
    const segText = displayText.slice(seg.start, seg.end);
    return (
      <Box
        key={key}
        component="mark"
        data-start={seg.start}
        data-co-anchor-ids={seg.anchors
          .map((a) => a.commentId)
          .filter(Boolean)
          .join(" ")}
        sx={{
          backgroundColor: alpha(color, 0.28),
          borderBottom: `2px solid ${color}`,
          padding: "0 1px",
          cursor: "pointer",
          borderRadius: 0.5,
        }}
        onClick={() => {
          const id = seg.anchors[0]?.commentId;
          if (id) scrollToComment(id);
        }}
      >
        {segText}
      </Box>
    );
  };

  const popoverAnchor = pendingMouseRef.current
    ? { top: pendingMouseRef.current.y + 4, left: pendingMouseRef.current.x }
    : { top: 0, left: 0 };

  const threadByParentId = useMemo(() => {
    const m = new Map<string, (typeof orphanThreads)[number]>();
    for (const t of orphanThreads) m.set(t.parent.commentId, t);
    return m;
  }, [orphanThreads]);

  // Render one thread (parent + replies) as a block-level card. Used by the
  // segments map below to inject comments into the narrative flow right after
  // the highlight they anchor to (Bitbucket-style inline review comments).
  const renderThreadBlock = (
    thread: (typeof orphanThreads)[number],
    keyHint: string,
  ) => {
    const { parent, replies } = thread;
    const resolved = !!parent.resolvedAt;
    const collapsed = isCollapsed(parent.commentId, resolved);
    const isReplying = orphanReplyingTo === parent.commentId;
    const quotedSnippet = (() => {
      if (parent.quotedText) return stripCitationTokens(parent.quotedText);
      const a = resolveAnchor(
        displayText,
        parent.charStart,
        parent.charEnd,
        parent.quotedText,
      );
      return a ? displayText.slice(a.start, a.end) : "";
    })();
    return (
      <Box
        key={keyHint}
        data-co-thread-id={parent.commentId}
        // display: block breaks the narrative's pre-wrap inline flow so the
        // card sits between text segments instead of beside them.
        sx={{ display: "block", my: 1 }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderRadius: 1,
            border: `1px solid ${theme.palette.warning.main}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            opacity: resolved ? 0.65 : 1,
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={collapsed ? 0 : 0.5}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {formatCommentAuthor(parent, reportOwnerUserId)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(parent.createdAt).format("MMM D, h:mm A")} · cycle{" "}
              {parent.reviewCycle}
            </Typography>
            {collapsed && replies.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                · {replies.length}{" "}
                {replies.length === 1 ? "reply" : "replies"}
              </Typography>
            )}
            <Box
              sx={{
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: 0.25,
              }}
            >
              {!collapsed &&
                mode === "officer" &&
                !parent.resolvedAt &&
                onFixWithAiComment && (
                  <Tooltip title="Implement suggestion with AI">
                    <span>
                      <IconButton
                        size="small"
                        disabled={isFixing}
                        onClick={() => handleFixClick(parent.commentId)}
                      >
                        {isFixing ? (
                          <CircularProgress size={14} />
                        ) : (
                          <AutoAwesomeIcon
                            fontSize="small"
                            sx={{ color: theme.palette.primary.main }}
                          />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              {!collapsed && replyEnabled && (
                <Tooltip title="Reply">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setOrphanReplyingTo(
                        isReplying ? null : parent.commentId,
                      )
                    }
                  >
                    <ReplyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {(mode === "officer" || mode === "co-edit") && (
                <Tooltip
                  title={
                    parent.resolvedAt ? "Mark unresolved" : "Mark resolved"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() =>
                      parent.resolvedAt
                        ? handleUnresolveClick(parent.commentId)
                        : handleResolveClick(parent.commentId)
                    }
                  >
                    {parent.resolvedAt ? (
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{ color: theme.palette.success.main }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={collapsed ? "Expand" : "Collapse"}>
                <IconButton
                  size="small"
                  onClick={() =>
                    toggleCollapsed(parent.commentId, resolved)
                  }
                >
                  {collapsed ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ExpandLessIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {!collapsed && quotedSnippet && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontStyle: "italic",
                color: "text.secondary",
                borderLeft: `2px solid ${alpha(theme.palette.warning.main, 0.5)}`,
                pl: 1,
                my: 0.5,
                whiteSpace: "pre-wrap",
              }}
            >
              &ldquo;{quotedSnippet}&rdquo;
            </Typography>
          )}
          {!collapsed && (
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {parent.commentText}
            </Typography>
          )}
        </Paper>

        {!collapsed && (replies.length > 0 || isReplying) && (
          <Stack
            spacing={0.75}
            sx={{
              mt: 0.5,
              pl: 2,
              borderLeft: `2px solid ${alpha(theme.palette.divider, 0.7)}`,
            }}
          >
            {replies.map((r) => (
              <Box
                key={r.commentId}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, fontSize: "0.7rem" }}
                  >
                    {formatCommentAuthor(r, reportOwnerUserId)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.65rem" }}
                  >
                    {dayjs(r.createdAt).format("MMM D, h:mm A")}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {r.commentText}
                </Typography>
              </Box>
            ))}
            {isReplying && onReplyComment && (
              <CommentReplyComposer
                onSubmit={async (text) => {
                  await onReplyComment(parent.commentId, text);
                  setOrphanReplyingTo(null);
                }}
                onCancel={() => setOrphanReplyingTo(null)}
              />
            )}
          </Stack>
        )}
      </Box>
    );
  };

  // Walk segments in order. After each mark, inject any threads anchored to
  // it (first occurrence wins, so a thread whose range spans multiple marks
  // is rendered just once at the first mark). Any threads with no anchor at
  // all spill out after the narrative.
  const renderNarrativeWithInlineThreads = () => {
    if (segments.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ fontStyle: "italic" }}>
          (Empty narrative)
        </Typography>
      );
    }
    const rendered = new Set<string>();
    const out: React.ReactNode[] = [];
    segments.forEach((seg, i) => {
      if (seg.kind === "mark") {
        out.push(renderMark(seg, i));
        for (const a of seg.anchors) {
          if (!a.commentId || rendered.has(a.commentId)) continue;
          const thread = threadByParentId.get(a.commentId);
          if (!thread) continue;
          rendered.add(a.commentId);
          out.push(renderThreadBlock(thread, `inline-${a.commentId}`));
        }
      } else {
        out.push(
          <Box
            component="span"
            key={i}
            data-start={seg.start}
            sx={{ display: "inline" }}
          >
            {displayText.slice(seg.start, seg.end)}
          </Box>,
        );
      }
    });
    for (const t of orphanThreads) {
      if (rendered.has(t.parent.commentId)) continue;
      out.push(renderThreadBlock(t, `unanchored-${t.parent.commentId}`));
    }
    return out;
  };

  return (
    <Box>
      <Box
        ref={containerRef}
        onMouseUp={handleMouseUp}
        sx={{
          whiteSpace: "pre-wrap",
          fontSize: "0.95rem",
          lineHeight: 1.6,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper",
          userSelect: "text",
        }}
      >
        {renderNarrativeWithInlineThreads()}
      </Box>


      {/* Compose popover (CO-edit only) */}
      <Popover
        open={!!popover}
        anchorReference="anchorPosition"
        anchorPosition={popoverAnchor}
        onClose={() => {
          setPopover(null);
          pendingMouseRef.current = null;
          if (typeof window !== "undefined") window.getSelection()?.removeAllRanges();
        }}
      >
        <Paper sx={{ p: 1.5, width: 320 }} elevation={0}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 600,
              color: "warning.dark",
              mb: 0.5,
            }}
          >
            New comment on highlighted text
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              fontStyle: "italic",
              mb: 1,
              maxHeight: 60,
              overflow: "auto",
              borderLeft: `3px solid ${alpha(theme.palette.warning.main, 0.4)}`,
              pl: 1,
            }}
          >
            “{popover?.quotedText}”
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            size="small"
            placeholder="Comment for the officer..."
            value={popover?.draft ?? ""}
            onChange={(e) =>
              setPopover((p) => (p ? { ...p, draft: e.target.value } : p))
            }
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
            <Button size="small" onClick={() => setPopover(null)}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              disabled={!popover?.draft.trim()}
              onClick={handleSubmitPopover}
            >
              Add comment
            </Button>
          </Stack>
        </Paper>
      </Popover>

    </Box>
  );
};
